import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SubSink } from 'subsink';
import {
  NbDialogService,
  NbMenuService,
  NbPopoverDirective,
} from '@nebular/theme';
import { Apollo } from 'apollo-angular';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { QueueService } from '../../../../shared/component/grid-queues/services/queue.service';
import { DynamicListViewComponent } from '../../../../shared/component/dynamiclistview/dynamiclistview.component';
import { MUTATION_RUN_EQUIPMENT_COUNTING_JOURNAL_VARIANCE } from '../../../../@core/graphql/mutations/equipment/runEquipmentCountingJournalVariance.mutation';
import { MUTATION_COMPLETE_EQUIPMENT_COUNTING_JOURNAL } from '../../../../@core/graphql/mutations/equipment/completeEquipmentCountingJournal.mutation';
import { MUTATION_CANCEL_EQUIPMENT_COUNTING_JOURNAL } from '../../../../@core/graphql/mutations/equipment/cancelEquipmentCountingJournal.mutation';
import { MUTATION_DELETE_EQUIPMENT_COUNTING_JOURNAL } from '../../../../@core/graphql/mutations/equipment/deleteEquipmentCountingJournal.mutation';
import { MUTATION_COUNT_EQUIPMENT_BY_BARCODE } from '../../../../@core/graphql/mutations/equipment/countEquipmentByBarcode.mutation';
import { MUTATION_DELETE_EQUIPMENT_COUNTING_JOURNAL_LINE } from '../../../../@core/graphql/mutations/equipment/deleteEquipmentCountingJournalLine.mutation';
import { QUERY_GET_EQUIPMENT_COUNTING_JOURNAL_USER_COUNTS } from '../../../../@core/graphql/queries/equipment/getEquipmentCountingJournalUserCounts.query';
import { QUERY_GetEquipmentCountingJournalById } from '../../../../@core/graphql/queries/equipment/getEquipmentCountingJournalById.query';
import { QUERY_GetEquipmentCountingJournalShareLink } from '../../../../@core/graphql/queries/equipment/getEquipmentCountingJournalShareLink.query';
import { GridMenuButton } from '../../../../shared/component/dynamiclistview/components/grid-menu/grid-menu.component';
import { WindowService } from '../../../../@core/services/window.service';
import { JournalLineDetailComponent } from '../journal-line-detail/journal-line-detail.component';

interface KpiTile {
  title: string;
  count: number;
  status: string;
  icon: string;
  filterValue: string;
  loading: boolean;
  headerField?: string; // Field name on journal header for this count
}

interface UserCount {
  userId: string;
  userName: string;
  count: number;
}

@Component({
    selector: 'journal-details',
    templateUrl: './journal-details.component.html',
    styleUrls: ['./journal-details.component.scss'],
    standalone: false
})
export class JournalDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('journalLinesList') journalLinesList: DynamicListViewComponent;
  @ViewChild('equipmentListDialog') equipmentListDialog: TemplateRef<any>;
  @ViewChild('writeInDialog') writeInDialog: TemplateRef<any>;

  @Input() journalId: string;
  @Input() journalName: string;
  @Input() journalStatus: string;
  @Output() refreshRequested = new EventEmitter<void>();

  windowRef: any;

  private sub = new SubSink();
  private readonly destroy$ = new Subject<void>();
  isRunningVariance = false;
  isConfirmingVariance = false;
  isCompletingCount = false;
  isCancellingJournal = false;
  isDeletingJournal = false;
  isDeletingLine = false;
  isAddingEquipment = false;
  isAddingWriteIn = false;
  isLoadingShareLink = false;
  shareLinkURL: string | null = null;
  shareLinkCopied = false;
  batchShareLinkURL: string | null = null;
  batchShareLinkCopied = false;
  selectedEquipmentToAdd: any[] = [];
  selectedLine: any = null;
  selectedWriteInStation: any = null;
  writeInActualStation = '';
  writeInActualLocationNum = '';

  // Context menu for adding equipment
  addEquipmentMenuItems = [
    { title: 'Equipment List', icon: 'list-outline' },
    { title: 'Write-In', icon: 'edit-outline' },
  ];

  // Write-in equipment form
  writeInForm: FormGroup;

  // User counts KPI
  userCounts: UserCount[] = [];
  userCountsLoading = true;
  userCountsSortAscending = false; // false = descending (more to less), true = ascending (less to more)

  buttons: GridMenuButton[] = [
    {
      label: 'Run Variance',
      icon: 'fa-exclamation-triangle',
      iconColor: 'orange', // blue
      type: 'button',
    },
    {
      label: 'Refresh',
      icon: 'fa-sync',
      iconColor: '#3366ff', // gray
      type: 'button',
    },
    {
      label: 'Complete Count',
      icon: 'fa-check',
      iconColor: '#00d68f', // green
      type: 'button',
    },
    {
      label: 'Cancel',
      icon: 'fa-ban',
      iconColor: '#ff3d71', // red
      type: 'button',
    },
    {
      label: 'Delete',
      icon: 'fa-trash',
      iconColor: '#ff3d71', // red
      type: 'button',
    },
    {
      label: 'Found Equipment',
      icon: 'fa-plus',
      iconColor: '#00d68f', // green
      type: 'button',
    },
    {
      label: 'Delete Line',
      icon: 'fa-trash',
      iconColor: '#ff3d71', // red
      type: 'button',
    },
    {
      label: 'Share',
      icon: 'fa-share',
      type: 'button',
    },
  ];

  kpiTiles: KpiTile[] = [
    {
      title: 'Not Found',
      count: 0,
      status: 'danger',
      icon: 'fa-question-circle',
      filterValue: 'Not Found',
      loading: true,
      headerField: 'countNotFound',
    },
    {
      title: 'Missing',
      count: 0,
      status: 'danger',
      icon: 'fa-times-circle',
      filterValue: 'Missing',
      loading: true,
      headerField: 'countMissing',
    },
    {
      title: 'Waiting Count',
      count: 0,
      status: 'warning',
      icon: 'fa-clock',
      filterValue: 'Waiting Count',
      loading: true,
      headerField: 'countWaitingCount',
    },
    {
      title: 'Counted',
      count: 0,
      status: 'success',
      icon: 'fa-check-circle',
      filterValue: 'Counted,Added',
      loading: true,
      headerField: 'countedCount',
    },
    {
      title: 'Added',
      count: 0,
      status: 'info',
      icon: 'fa-plus-circle',
      filterValue: 'Added',
      loading: true,
      headerField: 'countAdded',
    },

    {
      title: 'Other Location',
      count: 0,
      status: 'secondary',
      icon: 'fa-map-marker-alt',
      filterValue: 'Other Location',
      loading: true,
      headerField: 'countOtherLocation',
    },
  ];

  // Total KPI tile (separate from filtered tiles)
  totalKpiTile: KpiTile = {
    title: 'Total',
    count: 0,
    status: 'primary',
    icon: 'fa-list',
    filterValue: '',
    loading: true,
    headerField: 'lineCount',
  };

  filters: any[] = [];
  activeFilter: string | null = null;

  constructor(
    private queueService: QueueService,
    private dialogService: NbDialogService,
    private apollo: Apollo,
    private windowService: WindowService,
    private nbMenuService: NbMenuService,
    private fb: FormBuilder,
  ) {
    this.createWriteInForm();
  }

  ngOnInit(): void {
    if (this.journalId) {
      this.filters = [
        {
          column: 'journalId',
          value: this.journalId,
        },
      ];
      this.loadJournalData();
      this.loadKpiCounts();
      this.loadUserCounts();
    }

    // Subscribe to context menu item clicks
    this.nbMenuService
      .onItemClick()
      .pipe(
        filter(({ tag }) => tag === 'addEquipmentMenu'),
        map(({ item: { title } }) => title),
        takeUntil(this.destroy$),
      )
      .subscribe((title) => {
        if (title === 'Equipment List') {
          this.showEquipmentListPopover();
        } else if (title === 'Write-In') {
          this.showWriteInPopover();
        }
      });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Dialog reference for closing
  private equipmentDialogRef: any;
  private writeInDialogRef: any;

  /**
   * Creates the write-in equipment form
   */
  createWriteInForm(): void {
    this.writeInForm = this.fb.group({
      equipmentSerial: ['', Validators.required],
      equipmentMake: [''],
      equipmentModel: [''],
      notes: [''],
    });
  }

  /**
   * Handles station selection change for write-in equipment
   */
  onWriteInStationChange(stationObj: any): void {
    this.selectedWriteInStation = stationObj;
    this.writeInActualStation = stationObj?.station || '';

    // Update actual location based on the station's location
    if (stationObj?.locationRef?.locationNum) {
      this.writeInActualLocationNum = stationObj.locationRef.locationNum;
    } else if (stationObj?.locationId) {
      // If locationRef is not populated but locationId exists, use it as fallback
      this.writeInActualLocationNum = stationObj.locationId;
    } else {
      this.writeInActualLocationNum = '';
    }
  }

  /**
   * Shows the equipment list dialog
   */
  showEquipmentListPopover(): void {
    this.selectedEquipmentToAdd = [];
    this.equipmentDialogRef = this.dialogService.open(
      this.equipmentListDialog,
      {
        hasBackdrop: true,
        closeOnBackdropClick: true,
      },
    );
  }

  /**
   * Shows the write-in equipment dialog
   */
  showWriteInPopover(): void {
    this.writeInForm.reset();
    this.selectedWriteInStation = null;
    this.writeInActualStation = '';
    this.writeInActualLocationNum = '';
    this.writeInDialogRef = this.dialogService.open(this.writeInDialog, {
      hasBackdrop: true,
      closeOnBackdropClick: true,
    });
  }

  /**
   * Fetches the journal data from the server to get the latest status and counts
   */
  loadJournalData(): void {
    this.sub.sink = this.apollo
      .query<any>({
        query: QUERY_GetEquipmentCountingJournalById,
        variables: { id: this.journalId },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: ({ data }) => {
          const journal = data?.getEquipmentCountingJournal;
          if (journal) {
            this.journalStatus = journal.status;
            this.journalName = journal.name || this.journalName;
            if (this.windowRef?.config) {
              this.windowRef.config.title = `${journal?.journalNum} - ${this.journalName || ''}`;
            }

            // Update KPI counts from header fields
            this.updateKpiCountsFromHeader(journal);
          }
        },
        error: (error) => {
          console.error('Error loading journal data:', error);
        },
      });
  }

  /**
   * Updates KPI tile counts from journal header fields
   */
  private updateKpiCountsFromHeader(journal: any): void {
    // Update total count
    this.totalKpiTile.count = journal.lineCount || 0;
    this.totalKpiTile.loading = false;

    // Update each KPI tile from header fields
    this.kpiTiles.forEach((tile) => {
      if (tile.headerField && journal[tile.headerField] !== undefined) {
        tile.count = journal[tile.headerField] || 0;
      }
      tile.loading = false;
    });
  }

  loadKpiCounts(): void {
    // KPI counts are now loaded from the journal header in loadJournalData()
    // Set loading state for all tiles
    this.kpiTiles.forEach((tile) => {
      tile.loading = true;
    });
    this.totalKpiTile.loading = true;
  }

  loadUserCounts(): void {
    this.userCountsLoading = true;

    this.sub.sink = this.apollo
      .query<{ getEquipmentCountingJournalUserCounts: UserCount[] }>({
        query: QUERY_GET_EQUIPMENT_COUNTING_JOURNAL_USER_COUNTS,
        variables: { journalId: this.journalId },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: ({ data }) => {
          this.userCounts = data?.getEquipmentCountingJournalUserCounts || [];
          this.sortUserCounts();
          this.userCountsLoading = false;
        },
        error: () => {
          this.userCounts = [];
          this.userCountsLoading = false;
        },
      });
  }

  sortUserCounts(): void {
    this.userCounts = [...this.userCounts].sort((a, b) => {
      return this.userCountsSortAscending
        ? a.count - b.count
        : b.count - a.count;
    });
  }

  toggleUserCountsSort(): void {
    this.userCountsSortAscending = !this.userCountsSortAscending;
    this.sortUserCounts();
  }

  onUserChipClick(user: UserCount): void {
    const userFilterValue = `user:${user.userId}`;

    if (this.activeFilter === userFilterValue) {
      // Clear filter if clicking the same user chip
      this.activeFilter = null;
      this.filters = [
        {
          column: 'journalId',
          value: this.journalId,
        },
      ];
    } else {
      // Filter by countedBy user
      this.activeFilter = userFilterValue;
      this.filters = [
        {
          column: 'journalId',
          value: this.journalId,
        },
        {
          column: 'countedBy',
          value: user.userId,
        },
      ];
    }

    // Refresh the grid to apply the new filters
    if (this.journalLinesList) {
      this.journalLinesList.refreshGrid();
    }
  }

  clearUserFilter(): void {
    if (this.activeFilter?.startsWith('user:')) {
      this.activeFilter = null;
      this.filters = [
        {
          column: 'journalId',
          value: this.journalId,
        },
      ];

      // Refresh the grid to apply the cleared filters
      if (this.journalLinesList) {
        this.journalLinesList.refreshGrid();
      }
    }
  }

  onTileClick(tile: KpiTile): void {
    if (this.activeFilter === tile.filterValue) {
      // Clear filter if clicking the same tile
      this.activeFilter = null;
      this.filters = [
        {
          column: 'journalId',
          value: this.journalId,
        },
      ];
    } else {
      // Apply filter for the clicked tile
      this.activeFilter = tile.filterValue;
      this.filters = [
        {
          column: 'journalId',
          value: this.journalId,
        },
        {
          column: 'status',
          value: tile.filterValue,
        },
      ];
    }
  }

  onTotalTileClick(): void {
    // Clear any active filter to show all items
    this.activeFilter = null;
    this.filters = [
      {
        column: 'journalId',
        value: this.journalId,
      },
    ];

    // Refresh the grid to apply the cleared filters
    if (this.journalLinesList) {
      this.journalLinesList.refreshGrid();
    }
  }

  refreshData(): void {
    this.loadJournalData();
    this.loadKpiCounts();
    this.loadUserCounts();
    if (this.journalLinesList) {
      this.journalLinesList.refreshGrid();
    }
  }

  /**
   * Returns the count of items in Waiting status
   */
  getWaitingCount(): number {
    const waitingTile = this.kpiTiles.find(
      (tile) => tile.filterValue === 'Waiting',
    );
    return waitingTile?.count || 0;
  }

  /**
   * Opens the run variance confirmation dialog
   */
  runVarianceConfirm(dialog: TemplateRef<any>): void {
    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });
  }

  /**
   * Executes the run variance mutation
   */
  async runVariance(ref?: any): Promise<void> {
    if (!this.journalId || this.isConfirmingVariance) {
      return;
    }

    this.isConfirmingVariance = true;

    try {
      await this.apollo
        .mutate({
          mutation: MUTATION_RUN_EQUIPMENT_COUNTING_JOURNAL_VARIANCE,
          variables: { id: this.journalId },
        })
        .toPromise();

      // Refresh the data after successful variance run
      this.refreshData();

      // Update the journal status to reflect the change
      this.journalStatus = this.getWaitingCount() > 0 ? 'Recount' : 'Completed';

      // Emit refresh event to parent to refresh header
      this.refreshRequested.emit();
    } catch (error) {
      console.error('Error running variance:', error);
    } finally {
      this.isConfirmingVariance = false;
      ref?.close();
    }
  }

  /**
   * Handles refresh button click - refreshes header, lines and KPIs
   */
  handleRefreshClick(): void {
    this.refreshData();
    this.refreshRequested.emit();
  }

  /**
   * Opens the share dialog and fetches a fresh external share link
   * for this journal (mirrors the service-visit share link).
   */
  shareJournal(dialog: TemplateRef<any>): void {
    if (!this.journalId) {
      return;
    }

    this.shareLinkURL = null;
    this.shareLinkCopied = false;
    this.batchShareLinkURL = null;
    this.batchShareLinkCopied = false;
    this.isLoadingShareLink = true;

    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });

    this.apollo
      .query<any>({
        query: QUERY_GetEquipmentCountingJournalShareLink,
        variables: { id: this.journalId },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: (result) => {
          this.shareLinkURL =
            result?.data?.getEquipmentCountingJournal?.shareLinkURL || null;
          this.batchShareLinkURL =
            result?.data?.getEquipmentCountingJournal?.batchShareLinkURL ||
            null;
          this.isLoadingShareLink = false;
        },
        error: () => {
          this.isLoadingShareLink = false;
        },
      });
  }

  /**
   * Copies the share link to the clipboard
   */
  copyShareLink(): void {
    if (!this.shareLinkURL) {
      return;
    }
    navigator.clipboard.writeText(this.shareLinkURL);
    this.shareLinkCopied = true;
    this.batchShareLinkCopied = false;
  }

  /**
   * Copies the batch share link (all journals from the batch combined)
   */
  copyBatchShareLink(): void {
    if (!this.batchShareLinkURL) {
      return;
    }
    navigator.clipboard.writeText(this.batchShareLinkURL);
    this.batchShareLinkCopied = true;
    this.shareLinkCopied = false;
  }

  /**
   * Opens the complete count confirmation dialog
   */
  completeCountConfirm(dialog: TemplateRef<any>): void {
    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });
  }

  /**
   * Executes the complete count mutation
   */
  async completeCount(ref?: any): Promise<void> {
    if (!this.journalId || this.isCompletingCount) {
      return;
    }

    this.isCompletingCount = true;

    try {
      await this.apollo
        .mutate({
          mutation: MUTATION_COMPLETE_EQUIPMENT_COUNTING_JOURNAL,
          variables: { id: this.journalId },
        })
        .toPromise();

      // Update the journal status to reflect the change
      this.journalStatus = 'Completed';

      // Refresh the data after successful completion
      this.refreshData();

      // Emit refresh event to parent to refresh header
      this.refreshRequested.emit();
    } catch (error) {
      console.error('Error completing count:', error);
    } finally {
      this.isCompletingCount = false;
      ref?.close();
    }
  }

  /**
   * Opens the cancel journal confirmation dialog
   */
  cancelJournalConfirm(dialog: TemplateRef<any>): void {
    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });
  }

  /**
   * Executes the cancel journal mutation
   */
  async cancelJournal(ref?: any): Promise<void> {
    if (!this.journalId || this.isCancellingJournal) {
      return;
    }

    this.isCancellingJournal = true;

    try {
      await this.apollo
        .mutate({
          mutation: MUTATION_CANCEL_EQUIPMENT_COUNTING_JOURNAL,
          variables: {
            dto: {
              id: this.journalId,
              status: 'Cancelled',
            },
          },
        })
        .toPromise();

      // Update the journal status to reflect the change
      this.journalStatus = 'Cancelled';

      // Refresh the data after successful cancellation
      // this.refreshData();

      // Emit refresh event to parent to refresh header
      this.refreshRequested.emit();

      // Close the window after cancellation
      this.windowRef?.close({ refresh: true });
    } catch (error) {
      console.error('Error cancelling journal:', error);
    } finally {
      this.isCancellingJournal = false;
      ref?.close();
    }
  }

  /**
   * Opens the delete journal confirmation dialog
   */
  deleteJournalConfirm(dialog: TemplateRef<any>): void {
    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });
  }

  /**
   * Executes the delete journal mutation
   */
  async deleteJournal(ref?: any): Promise<void> {
    if (!this.journalId || this.isDeletingJournal) {
      return;
    }

    this.isDeletingJournal = true;

    try {
      await this.apollo
        .mutate({
          mutation: MUTATION_DELETE_EQUIPMENT_COUNTING_JOURNAL,
          variables: { id: this.journalId },
          update: (cache) => {
            // Remove the deleted journal from the Apollo cache
            cache.evict({
              id: cache.identify({
                __typename: 'EquipmentCountingJournal',
                id: this.journalId,
              }),
            });
            cache.gc();
          },
        })
        .toPromise();

      // Close the window after deletion
      this.windowRef?.close({ refresh: true });
    } catch (error) {
      console.error('Error deleting journal:', error);
    } finally {
      this.isDeletingJournal = false;
      ref?.close();
    }
  }

  /**
   * Handles double-click on a journal line to open line detail window
   */
  onLineDoubleClick(line: any): void {
    if (line) {
      const equipmentNum =
        line.equipment?.equipmentNum || line.equipmentNum || line.equipmentId;

      const windowRef = this.windowService.openWindow(
        JournalLineDetailComponent,
        {
          context: {
            lineData: line,
          },
          objectId: line.id,
          options: {
            title: `Journal Line - ${equipmentNum}`,
            windowClass: 'detail-window',
          },
        },
      );

      // Listen for window close and refresh grid if needed
      windowRef.onClose.subscribe((result: any) => {
        if (result?.refresh) {
          this.refreshData();
        }
      });
    }
  }

  /**
   * Handles selection of equipment to add to the journal
   */
  onSelectEquipmentToAdd(equipment: any[]): void {
    this.selectedEquipmentToAdd = equipment;
  }

  /**
   * Adds selected equipment to the journal as new lines
   */
  async addEquipmentToJournal(): Promise<void> {
    if (!this.selectedEquipmentToAdd?.length || this.isAddingEquipment) {
      return;
    }

    this.isAddingEquipment = true;

    try {
      // Add each selected equipment to the journal
      for (const equipment of this.selectedEquipmentToAdd) {
        await this.apollo
          .mutate({
            mutation: MUTATION_COUNT_EQUIPMENT_BY_BARCODE,
            variables: {
              dto: {
                journalId: this.journalId,
                equipmentId: equipment.id,
                actualQty: 1,
                scannedViaQRCode: false,
              },
            },
          })
          .toPromise();
      }

      // Refresh the data after adding equipment
      this.refreshData();

      // Clear selection and close dialog
      this.selectedEquipmentToAdd = [];
      if (this.equipmentDialogRef) {
        this.equipmentDialogRef.close();
      }
    } catch (error) {
      console.error('Error adding equipment to journal:', error);
    } finally {
      this.isAddingEquipment = false;
    }
  }

  /**
   * Closes the equipment dialog
   */
  closeEquipmentPopover(): void {
    this.selectedEquipmentToAdd = [];
    if (this.equipmentDialogRef) {
      this.equipmentDialogRef.close();
    }
  }

  /**
   * Closes the write-in dialog
   */
  closeWriteInPopover(): void {
    this.writeInForm.reset();
    if (this.writeInDialogRef) {
      this.writeInDialogRef.close();
    }
  }

  /**
   * Checks if the write-in form is valid
   */
  isWriteInFormValid(): boolean {
    return this.writeInForm.valid && !!this.writeInActualStation;
  }

  /**
   * Adds a write-in equipment to the journal
   */
  async addWriteInEquipment(): Promise<void> {
    if (!this.isWriteInFormValid() || this.isAddingWriteIn) {
      return;
    }

    this.isAddingWriteIn = true;

    try {
      const formValue = this.writeInForm.value;

      await this.apollo
        .mutate({
          mutation: MUTATION_COUNT_EQUIPMENT_BY_BARCODE,
          variables: {
            dto: {
              journalId: this.journalId,
              equipmentSerial: formValue.equipmentSerial,
              equipmentMake: formValue.equipmentMake || null,
              equipmentModel: formValue.equipmentModel || null,
              notes: formValue.notes || null,
              actualQty: 1,
              scannedViaQRCode: false,
              actualStation: this.writeInActualStation || null,
              actualLocationNum: this.writeInActualLocationNum || null,
            },
          },
        })
        .toPromise();

      // Refresh the data after adding equipment
      this.refreshData();

      // Reset form and close popover
      this.writeInForm.reset();
      this.closeWriteInPopover();
    } catch (error) {
      console.error('Error adding write-in equipment to journal:', error);
    } finally {
      this.isAddingWriteIn = false;
    }
  }

  /**
   * Handles line selection from the grid
   */
  onLineSelect(lines: any[]): void {
    // Get the first selected line (single selection)
    this.selectedLine = lines?.length > 0 ? lines[0] : null;
  }

  /**
   * Checks if the selected line can be deleted (only lines with status 'Added')
   */
  canDeleteSelectedLine(): boolean {
    return this.selectedLine?.status === 'Added';
  }

  /**
   * Opens the delete line confirmation dialog
   */
  deleteLineConfirm(dialog: TemplateRef<any>): void {
    if (!this.canDeleteSelectedLine()) {
      return;
    }
    this.dialogService.open(dialog, {
      hasBackdrop: false,
      closeOnBackdropClick: false,
    });
  }

  /**
   * Executes the delete line mutation
   */
  async deleteLine(ref?: any): Promise<void> {
    if (!this.selectedLine?.id || this.isDeletingLine) {
      return;
    }

    this.isDeletingLine = true;

    try {
      await this.apollo
        .mutate({
          mutation: MUTATION_DELETE_EQUIPMENT_COUNTING_JOURNAL_LINE,
          variables: { id: this.selectedLine.id },
          update: (cache) => {
            // Remove the deleted line from the Apollo cache
            cache.evict({
              id: cache.identify({
                __typename: 'EquipmentCountingJournalLine',
                id: this.selectedLine.id,
              }),
            });
            cache.gc();
          },
        })
        .toPromise();

      // Clear selection
      this.selectedLine = null;

      // Refresh the data after deletion
      this.refreshData();

      // Emit refresh event to parent to refresh header
      this.refreshRequested.emit();
    } catch (error) {
      console.error('Error deleting journal line:', error);
    } finally {
      this.isDeletingLine = false;
      ref?.close();
    }
  }
}
